import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Lock, LogOut, Loader2, Download, Inbox, PoundSterling, Settings2, Save,
  Newspaper, Plus, Pencil, Trash2, ArrowLeft, Bus, ImagePlus, Star, Eye, EyeOff,
  ArrowUp, ArrowDown, RefreshCw, AlertTriangle, Globe2, MoonStar, Store, Image as ImageIcon,
  Building2, UserPlus, FileText, Search,
} from "lucide-react";
import { ROLES, SERVICE_LIST } from "../components/calculators";
import Seo from "../components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-[#FAFAFC] border border-[#E2E8F0] rounded-xl px-4 py-3 text-sm text-[#0B192C] outline-none transition-colors focus:border-[#10B981] focus:bg-white";
const labelCls = "block text-xs font-bold uppercase tracking-wider text-[#475569] mb-1.5";

const STATUS_LABELS = {
  new: "New",
  contacted: "Contacted",
  proposal_sent: "Proposal sent",
  contract_agreed: "Contract agreed",
  unsuccessful: "Unsuccessful",
  sourcing: "Sourcing",
  pharmacist_proposed: "Pharmacist Proposed",
  confirmed: "Confirmed",
  filled: "Filled",
  closed: "Closed",
};
const STATUS_COLORS = {
  new: "#10B981",
  contacted: "#06B6D4",
  proposal_sent: "#F59E0B",
  contract_agreed: "#0B192C",
  unsuccessful: "#EF4444",
  sourcing: "#F59E0B",
  pharmacist_proposed: "#06B6D4",
  confirmed: "#6366F1",
  filled: "#10B981",
  closed: "#64748B",
};

const VACANCY_STATUSES = ["new", "sourcing", "pharmacist_proposed", "confirmed", "filled", "closed"];
const CLINIC_STATUSES = ["new", "contacted", "discovery_call", "under_review", "potential_partner", "agreed", "not_proceeding"];
STATUS_LABELS.discovery_call = "Discovery Call";
STATUS_LABELS.under_review = "Under Review";
STATUS_LABELS.potential_partner = "Potential Partner";
STATUS_LABELS.agreed = "Agreed";
STATUS_LABELS.not_proceeding = "Not Proceeding";
STATUS_COLORS.discovery_call = "#06B6D4";
STATUS_COLORS.under_review = "#F59E0B";
STATUS_COLORS.potential_partner = "#6366F1";
STATUS_COLORS.agreed = "#0B192C";
STATUS_COLORS.not_proceeding = "#64748B";
STATUS_LABELS.reviewing = "Reviewing";
STATUS_LABELS.shortlisted = "Shortlisted";
STATUS_LABELS.kept_on_file = "Kept on File";
STATUS_COLORS.reviewing = "#F59E0B";
STATUS_COLORS.shortlisted = "#6366F1";
STATUS_COLORS.kept_on_file = "#06B6D4";

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem("ncs_admin_token") || "");
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(!!token);
  const [tab, setTab] = useState("enquiries");

  const authed = useCallback(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/auth/me`, authed())
      .then((r) => setUser(r.data))
      .catch(() => {
        localStorage.removeItem("ncs_admin_token");
        setToken("");
        setUser(null);
      })
      .finally(() => setChecking(false));
  }, [token, authed]);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, authed());
    } catch {}
    localStorage.removeItem("ncs_admin_token");
    setToken("");
    setUser(null);
  };

  if (checking) {
    return (
      <main className="min-h-screen flex items-center justify-center pt-20" data-testid="admin-loading">
        <Loader2 size={28} className="animate-spin text-[#10B981]" />
      </main>
    );
  }

  if (!user) return <LoginForm onLogin={(t, u) => { localStorage.setItem("ncs_admin_token", t); setToken(t); setUser(u); }} />;

  return (
    <main className="min-h-screen pt-28 pb-20" data-testid="admin-dashboard">
      <Seo title="Staff Dashboard | NCS" noindex />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#10B981]">NCS Administration</p>
            <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tight text-[#0B192C]">
              Welcome back, {user.name}
            </h1>
          </div>
          <button
            data-testid="admin-logout-btn"
            onClick={logout}
            className="inline-flex items-center gap-2 text-sm font-bold text-[#475569] border border-[#E2E8F0] bg-white rounded-full px-5 py-2.5 hover:text-[#EF4444] hover:border-[#EF4444] transition-colors"
          >
            <LogOut size={15} /> Log out
          </button>
        </div>

        <div className="mt-8 flex flex-wrap gap-2.5" role="tablist" data-testid="admin-tabs">
          {[
            { k: "enquiries", label: "Enquiries", icon: Inbox },
            { k: "professionals", label: "International", icon: Globe2 },
            { k: "transport", label: "Transport", icon: Bus },
            { k: "ooh", label: "Out of Hours", icon: MoonStar },
            { k: "community", label: "Community Pharmacy", icon: Store },
            { k: "siteimages", label: "Site Images", icon: ImageIcon },
            { k: "clinic", label: "NCS Clinic", icon: Building2 },
            { k: "careers", label: "Careers", icon: UserPlus },
            { k: "articles", label: "Articles", icon: Newspaper },
            { k: "pricing", label: "Pricing", icon: PoundSterling },
            { k: "project", label: "Project Settings", icon: Settings2 },
          ].map((t) => (
            <button
              key={t.k}
              role="tab"
              aria-selected={tab === t.k}
              data-testid={`admin-tab-${t.k}`}
              onClick={() => setTab(t.k)}
              className={`inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-bold border transition-colors duration-300 ${
                tab === t.k
                  ? "bg-[#0B192C] text-white border-[#0B192C]"
                  : "bg-white text-[#475569] border-[#E2E8F0] hover:border-[#0B192C]"
              }`}
            >
              <t.icon size={15} /> {t.label}
            </button>
          ))}
        </div>

        <div className="mt-8">
          {tab === "enquiries" && <EnquiriesTab authed={authed} />}
          {tab === "professionals" && <ProfessionalsTab authed={authed} />}
          {tab === "transport" && <TransportTab authed={authed} />}
          {tab === "ooh" && <OohTab authed={authed} />}
          {tab === "community" && <CommunityTab authed={authed} />}
          {tab === "siteimages" && <SiteImagesTab authed={authed} />}
          {tab === "clinic" && <ClinicTab authed={authed} />}
          {tab === "careers" && <CareersTab authed={authed} />}
          {tab === "articles" && <ArticlesTab authed={authed} />}
          {tab === "pricing" && <PricingTab authed={authed} />}
          {tab === "project" && <ProjectSettingsTab authed={authed} />}
        </div>
      </div>
    </main>
  );
};

const LoginForm = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      onLogin(data.access_token, data.user);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center pt-20 px-4" data-testid="admin-login-page">
      <Seo title="Staff Login | NCS" noindex />
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-md bg-white border border-[#E2E8F0] rounded-[2rem] p-8 sm:p-10 shadow-[0_10px_30px_rgba(11,25,44,0.04)]"
      >
        <span className="w-12 h-12 rounded-2xl bg-[#0B192C] text-white flex items-center justify-center">
          <Lock size={20} />
        </span>
        <h1 className="mt-6 font-display text-2xl font-extrabold text-[#0B192C]">NCS Staff Login</h1>
        <p className="mt-2 text-sm text-[#64748B]">Authorised National Clinical Services staff only.</p>
        <div className="mt-7 space-y-4">
          <div>
            <label htmlFor="admin-email" className={labelCls}>Email</label>
            <input id="admin-email" data-testid="admin-input-email" type="email" required
              value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label htmlFor="admin-password" className={labelCls}>Password</label>
            <input id="admin-password" data-testid="admin-input-password" type="password" required
              value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} />
          </div>
        </div>
        {error && (
          <p data-testid="admin-login-error" className="mt-4 text-sm font-semibold text-[#EF4444]">{error}</p>
        )}
        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-btn"
          className="mt-7 w-full inline-flex items-center justify-center gap-2 bg-[#0B192C] text-white font-bold px-6 py-4 rounded-full text-sm transition-colors duration-300 hover:bg-[#10B981] disabled:opacity-60"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : "Sign in"}
        </button>
      </form>
    </main>
  );
};

const EnquiriesTab = ({ authed }) => {
  const [enquiries, setEnquiries] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/admin/enquiries`, authed()).then((r) => setEnquiries(r.data)).catch(() => toast.error("Could not load enquiries"));
  }, [authed]);

  useEffect(load, [load]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/enquiries/${id}`, { status }, authed());
      setEnquiries((list) => list.map((e) => (e.id === id ? { ...e, status } : e)));
      toast.success("Status updated");
    } catch {
      toast.error("Could not update status");
    }
  };

  const downloadCsv = async () => {
    try {
      const r = await axios.get(`${API}/admin/enquiries/csv`, { ...authed(), responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "ncs-enquiries.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("CSV download failed");
    }
  };

  if (!enquiries) return <Loader2 size={24} className="animate-spin text-[#10B981] mx-auto mt-16" />;

  return (
    <div data-testid="admin-enquiries">
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm font-semibold text-[#475569]">{enquiries.length} enquir{enquiries.length === 1 ? "y" : "ies"}</p>
        <button
          data-testid="admin-csv-btn"
          onClick={downloadCsv}
          className="inline-flex items-center gap-2 text-sm font-bold bg-[#0B192C] text-white rounded-full px-5 py-2.5 hover:bg-[#10B981] transition-colors"
        >
          <Download size={15} /> Download CSV
        </button>
      </div>
      {enquiries.length === 0 ? (
        <div className="bg-white border border-[#E2E8F0] rounded-3xl p-14 text-center text-sm text-[#64748B]" data-testid="admin-enquiries-empty">
          No enquiries yet. New submissions from the website will appear here.
        </div>
      ) : (
        <div className="space-y-4">
          {enquiries.map((e) => (
            <div key={e.id} data-testid={`enquiry-row-${e.id}`} className="bg-white border border-[#E2E8F0] rounded-3xl p-6 sm:p-7">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="font-display font-bold text-[#0B192C]">
                    {e.organisation} <span className="text-[#64748B] font-medium">· {e.org_type}</span>
                    {e.journey === "transport" && (
                      <span className="ml-2 text-[10px] font-extrabold uppercase tracking-wider bg-[#F59E0B]/15 text-[#B45309] rounded-full px-2.5 py-1">Transport</span>
                    )}
                    {e.journey === "international" && (
                      <span className="ml-2 text-[10px] font-extrabold uppercase tracking-wider bg-[#06B6D4]/15 text-[#0E7490] rounded-full px-2.5 py-1">International</span>
                    )}
                    {e.journey === "out-of-hours" && (
                      <span className="ml-2 text-[10px] font-extrabold uppercase tracking-wider bg-[#6366F1]/15 text-[#4338CA] rounded-full px-2.5 py-1">Out of Hours</span>
                    )}
                    {e.journey === "community-pharmacy" && (
                      <span className="ml-2 text-[10px] font-extrabold uppercase tracking-wider bg-[#10B981]/15 text-[#0d9668] rounded-full px-2.5 py-1">Pharmacy Vacancy</span>
                    )}
                    {e.journey === "preferred-supplier" && (
                      <span className="ml-2 text-[10px] font-extrabold uppercase tracking-wider bg-[#0B192C]/10 text-[#0B192C] rounded-full px-2.5 py-1">Preferred Supplier</span>
                    )}
                    {e.journey === "ncs-clinic" && (
                      <span className="ml-2 text-[10px] font-extrabold uppercase tracking-wider bg-[#F59E0B]/15 text-[#B45309] rounded-full px-2.5 py-1">NCS Clinic EOI</span>
                    )}
                  </p>
                  <p className="text-sm text-[#475569] mt-1">{e.full_name} — {e.email} — {e.phone}</p>
                  <p className="text-xs text-[#94A3B8] mt-1">{new Date(e.created_at).toLocaleString("en-GB")} · {e.journey}</p>
                </div>
                <select
                  data-testid={`enquiry-status-${e.id}`}
                  value={e.status}
                  onChange={(ev) => setStatus(e.id, ev.target.value)}
                  className="text-xs font-bold rounded-full px-4 py-2 border-2 outline-none cursor-pointer bg-white"
                  style={{ borderColor: STATUS_COLORS[e.status], color: STATUS_COLORS[e.status] }}
                >
                  {Object.entries(STATUS_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </div>
              <p className="mt-4 text-sm font-semibold text-[#0B192C]">{e.requirement}</p>
              {e.additional && <p className="mt-2 text-sm text-[#475569]">{e.additional}</p>}
              {e.calculator?.length > 0 && (
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-2 bg-[#F8FAFC] rounded-2xl p-4">
                  {e.calculator.map((c) => (
                    <div key={c.label}>
                      <p className="text-[10px] uppercase tracking-wider text-[#94A3B8]">{c.label}</p>
                      <p className="text-xs font-bold text-[#0B192C] mt-0.5">{c.value}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const PROF_STATUS_LABELS = {
  new: "New",
  contacted: "Contacted",
  kept_on_file: "Kept on file",
  unsuccessful: "Unsuccessful",
};

const PROF_STATUS_COLORS = {
  new: "#10B981",
  contacted: "#06B6D4",
  kept_on_file: "#F59E0B",
  unsuccessful: "#EF4444",
};

const ProfessionalsTab = ({ authed }) => {
  const [entries, setEntries] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/admin/professionals`, authed()).then((r) => setEntries(r.data)).catch(() => toast.error("Could not load professional interest"));
  }, [authed]);

  useEffect(load, [load]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/professionals/${id}`, { status }, authed());
      setEntries((list) => list.map((e) => (e.id === id ? { ...e, status } : e)));
      toast.success("Status updated");
    } catch {
      toast.error("Could not update status");
    }
  };

  if (!entries) return <Loader2 size={24} className="animate-spin text-[#10B981] mx-auto mt-16" />;

  return (
    <div data-testid="admin-professionals">
      <div className="mb-5">
        <p className="text-sm font-semibold text-[#475569]">{entries.length} professional{entries.length === 1 ? "" : "s"} registered</p>
        <p className="text-xs text-[#94A3B8] mt-1">
          Expressions of interest from the NCS International page. Partnership enquiries from organisations
          appear in the Enquiries tab with an International badge.
        </p>
      </div>
      {entries.length === 0 ? (
        <div className="bg-white border border-[#E2E8F0] rounded-3xl p-14 text-center text-sm text-[#64748B]" data-testid="admin-professionals-empty">
          No professional interest registered yet. New registrations from /international will appear here.
        </div>
      ) : (
        <div className="space-y-4">
          {entries.map((e) => (
            <div key={e.id} data-testid={`professional-row-${e.id}`} className="bg-white border border-[#E2E8F0] rounded-3xl p-6 sm:p-7">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="font-display font-bold text-[#0B192C]">
                    {e.full_name} <span className="text-[#64748B] font-medium">· {e.role}</span>
                  </p>
                  <p className="text-sm text-[#475569] mt-1">{e.email} — {e.country_of_residence}</p>
                  <p className="text-xs text-[#94A3B8] mt-1">{new Date(e.created_at).toLocaleString("en-GB")}</p>
                </div>
                <select
                  data-testid={`professional-status-${e.id}`}
                  value={e.status}
                  onChange={(ev) => setStatus(e.id, ev.target.value)}
                  className="text-xs font-bold rounded-full px-4 py-2 border-2 outline-none cursor-pointer bg-white"
                  style={{ borderColor: PROF_STATUS_COLORS[e.status], color: PROF_STATUS_COLORS[e.status] }}
                >
                  {Object.entries(PROF_STATUS_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </div>
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-2 bg-[#F8FAFC] rounded-2xl p-4">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-[#94A3B8]">Regulator</p>
                  <p className="text-xs font-bold text-[#0B192C] mt-0.5">{e.regulator || "-"}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-[#94A3B8]">Registration status</p>
                  <p className="text-xs font-bold text-[#0B192C] mt-0.5">{e.registration_status || "-"}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-[#94A3B8]">Countries of interest</p>
                  <p className="text-xs font-bold text-[#0B192C] mt-0.5">{e.countries_of_interest || "-"}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-[#94A3B8]">Experience / preference</p>
                  <p className="text-xs font-bold text-[#0B192C] mt-0.5">{e.experience_years || "-"} · {e.preference || "-"}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const OohTab = ({ authed }) => {
  const [content, setContent] = useState(null);
  const [uploading, setUploading] = useState(null);
  const [bust, setBust] = useState(0);
  const fileRefs = useRef({});

  const load = useCallback(() => {
    axios.get(`${API}/out-of-hours/content`).then((r) => setContent(r.data)).catch(() => toast.error("Could not load Out of Hours content"));
  }, []);

  useEffect(load, [load]);

  const upload = async (slot, file) => {
    if (!file) return;
    setUploading(slot);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/admin/out-of-hours/image/${slot}`, fd, authed());
      toast.success("Image replaced — live on the Out of Hours page now");
      setBust((b) => b + 1);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(null);
    }
  };

  const reset = async (slot) => {
    setUploading(slot);
    try {
      await axios.delete(`${API}/admin/out-of-hours/image/${slot}`, authed());
      toast.success("Reset to the default image");
      setBust((b) => b + 1);
      load();
    } catch {
      toast.error("Could not reset image");
    } finally {
      setUploading(null);
    }
  };

  if (!content) return <Loader2 size={24} className="animate-spin text-[#10B981] mx-auto mt-16" />;

  const slots = [
    { key: "hero", label: "Hero photograph", desc: "Night-time medication delivery scene shown at the top of the page.", custom: content.hero_custom },
    { key: "about", label: "About section photograph", desc: "Pharmacy medicines preparation image beside the service description.", custom: content.about_custom },
  ];

  return (
    <div data-testid="admin-ooh">
      <div className="mb-5">
        <p className="text-sm font-semibold text-[#475569]">NCS Out of Hours — page images</p>
        <p className="text-xs text-[#94A3B8] mt-1">
          Replace the photographs on /out-of-hours with approved NCS images. Uploads are optimised to
          WebP and go live immediately. Out-of-hours enquiries appear in the Enquiries tab with an
          "Out of Hours" badge.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {slots.map((s) => (
          <div key={s.key} className="bg-white border border-[#E2E8F0] rounded-3xl p-6">
            <img
              src={s.custom ? `${API}/out-of-hours/image/${s.key}?v=${bust}` : `/assets/ooh-${s.key}.png`}
              onError={(e) => { const fallback = `/assets/ooh-${s.key}.png`; if (!e.currentTarget.src.endsWith(fallback)) e.currentTarget.src = fallback; }}
              alt={s.label}
              data-testid={`ooh-admin-image-${s.key}`}
              className="w-full h-48 object-cover rounded-2xl border border-[#E2E8F0]"
            />
            <div className="mt-4 flex items-center justify-between gap-3">
              <div>
                <p className="font-display font-bold text-[#0B192C] text-sm">{s.label}</p>
                <p className="text-xs text-[#64748B] mt-0.5">{s.desc}</p>
                <p className="text-[10px] uppercase tracking-wider font-bold mt-1.5" style={{ color: s.custom ? "#10B981" : "#94A3B8" }}>
                  {s.custom ? "Custom NCS image active" : "Default image active"}
                </p>
              </div>
              <div className="shrink-0 flex flex-col items-end gap-2">
                <button
                  data-testid={`ooh-upload-${s.key}`}
                  onClick={() => fileRefs.current[s.key]?.click()}
                  disabled={uploading === s.key}
                  className="inline-flex items-center gap-2 bg-[#0B192C] text-white text-xs font-bold rounded-full px-4 py-2.5 hover:bg-[#10B981] transition-colors disabled:opacity-60"
                >
                  {uploading === s.key ? <Loader2 size={13} className="animate-spin" /> : <ImagePlus size={13} />} Replace
                </button>
                {s.custom && (
                  <button
                    data-testid={`ooh-reset-${s.key}`}
                    onClick={() => reset(s.key)}
                    disabled={uploading === s.key}
                    className="text-[11px] font-bold text-[#64748B] hover:text-[#EF4444] transition-colors disabled:opacity-60"
                  >
                    Reset to default
                  </button>
                )}
              </div>
            </div>
            <input
              ref={(el) => (fileRefs.current[s.key] = el)}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              data-testid={`ooh-file-${s.key}`}
              onChange={(e) => { upload(s.key, e.target.files?.[0]); e.target.value = ""; }}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const CommunityTab = ({ authed }) => {
  const [enquiries, setEnquiries] = useState(null);
  const [settings, setSettings] = useState(null);
  const [filter, setFilter] = useState("all");
  const [notesDraft, setNotesDraft] = useState({});
  const [savingNote, setSavingNote] = useState(null);
  const [savingFee, setSavingFee] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/admin/enquiries`, authed()).then((r) => setEnquiries(r.data)).catch(() => toast.error("Could not load enquiries"));
    axios.get(`${API}/admin/settings`, authed()).then((r) => setSettings(r.data)).catch(() => {});
  }, [authed]);

  useEffect(load, [load]);

  if (!enquiries || !settings) return <Loader2 size={24} className="animate-spin text-[#10B981] mx-auto mt-16" />;

  const vacancies = enquiries.filter((e) => e.journey === "community-pharmacy");
  const suppliers = enquiries.filter((e) => e.journey === "preferred-supplier");
  const isUrgent = (e) => (e.requirement || "").toLowerCase().includes("urgent");
  const visible = vacancies.filter((e) =>
    filter === "all" ? true : filter === "urgent" ? isUrgent(e) : !isUrgent(e)
  );

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/enquiries/${id}`, { status }, authed());
      setEnquiries((list) => list.map((e) => (e.id === id ? { ...e, status } : e)));
      toast.success("Status updated");
    } catch {
      toast.error("Could not update status");
    }
  };

  const saveNote = async (id) => {
    setSavingNote(id);
    try {
      await axios.patch(`${API}/admin/enquiries/${id}/commercial`, { commercial_notes: notesDraft[id] ?? "" }, authed());
      setEnquiries((list) => list.map((e) => (e.id === id ? { ...e, commercial_notes: notesDraft[id] ?? "" } : e)));
      toast.success("Commercial terms saved (private)");
    } catch {
      toast.error("Could not save commercial terms");
    } finally {
      setSavingNote(null);
    }
  };

  const saveFees = async () => {
    setSavingFee(true);
    try {
      const { data } = await axios.put(`${API}/admin/settings`, {
        pharmacy_sourcing_fee_public: settings.pharmacy_sourcing_fee_public || "",
        pharmacy_sourcing_fee_private: settings.pharmacy_sourcing_fee_private || "",
      }, authed());
      setSettings(data);
      toast.success("Sourcing fee settings saved");
    } catch {
      toast.error("Could not save fee settings");
    } finally {
      setSavingFee(false);
    }
  };

  const downloadCsv = async () => {
    try {
      const r = await axios.get(`${API}/admin/enquiries/csv?journey=community-pharmacy`, { ...authed(), responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "ncs-community-pharmacy-vacancies.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("CSV download failed");
    }
  };

  return (
    <div data-testid="admin-community">
      {/* Fee configuration */}
      <div className="bg-white border border-[#E2E8F0] rounded-3xl p-7 mb-8">
        <h3 className="font-display text-lg font-bold text-[#0B192C]">Sourcing fee configuration</h3>
        <p className="text-xs text-[#64748B] mt-1">
          The public display text appears on the Community Pharmacy page only while it contains a value.
          Private notes are never shown publicly.
        </p>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Public sourcing fee display (optional)</label>
            <input data-testid="cp-fee-public-input"
              value={settings.pharmacy_sourcing_fee_public || ""}
              onChange={(e) => setSettings((s) => ({ ...s, pharmacy_sourcing_fee_public: e.target.value }))}
              placeholder='e.g. "Fixed sourcing fee: £95 per agreed placement"' className={inputCls} />
            <p className="text-[10px] text-[#94A3B8] mt-1">Leave blank to keep the fee unpublished.</p>
          </div>
          <div>
            <label className={labelCls}>Private fee notes & margins (admin only)</label>
            <textarea data-testid="cp-fee-private-input" rows={2}
              value={settings.pharmacy_sourcing_fee_private || ""}
              onChange={(e) => setSettings((s) => ({ ...s, pharmacy_sourcing_fee_private: e.target.value }))}
              placeholder="Internal fee schedule, margins, negotiated rates — never public" className={`${inputCls} resize-none`} />
          </div>
        </div>
        <button data-testid="cp-fee-save-btn" onClick={saveFees} disabled={savingFee}
          className="mt-5 inline-flex items-center gap-2 bg-[#0B192C] text-white font-bold px-6 py-3 rounded-full text-sm hover:bg-[#10B981] transition-colors disabled:opacity-60">
          {savingFee ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save fee settings
        </button>
      </div>

      {/* Vacancies */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
        <div className="flex items-center gap-2.5">
          <p className="text-sm font-semibold text-[#475569]">{vacancies.length} vacanc{vacancies.length === 1 ? "y" : "ies"}</p>
          {[
            { k: "all", l: "All" },
            { k: "urgent", l: "Urgent" },
            { k: "standard", l: "Standard" },
          ].map((f) => (
            <button key={f.k} data-testid={`cp-filter-${f.k}`} onClick={() => setFilter(f.k)}
              className={`px-4 py-2 rounded-full text-xs font-bold border transition-colors ${
                filter === f.k ? "bg-[#0B192C] text-white border-[#0B192C]" : "bg-white text-[#475569] border-[#E2E8F0] hover:border-[#0B192C]"
              }`}>
              {f.l}
            </button>
          ))}
        </div>
        <button data-testid="cp-csv-btn" onClick={downloadCsv}
          className="inline-flex items-center gap-2 text-sm font-bold bg-[#0B192C] text-white rounded-full px-5 py-2.5 hover:bg-[#10B981] transition-colors">
          <Download size={15} /> Vacancies CSV
        </button>
      </div>

      {visible.length === 0 ? (
        <div className="bg-white border border-[#E2E8F0] rounded-3xl p-14 text-center text-sm text-[#64748B]" data-testid="cp-vacancies-empty">
          No vacancies in this view. New submissions from /community-pharmacy will appear here.
        </div>
      ) : (
        <div className="space-y-4">
          {visible.map((e) => (
            <div key={e.id} data-testid={`cp-vacancy-row-${e.id}`} className="bg-white border border-[#E2E8F0] rounded-3xl p-6 sm:p-7">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="font-display font-bold text-[#0B192C]">
                    {e.organisation}
                    {isUrgent(e) && (
                      <span data-testid={`cp-urgent-badge-${e.id}`} className="ml-2 text-[10px] font-extrabold uppercase tracking-wider bg-[#EF4444]/15 text-[#EF4444] rounded-full px-2.5 py-1">Urgent</span>
                    )}
                  </p>
                  <p className="text-sm text-[#475569] mt-1">{e.full_name} — {e.email} — {e.phone}</p>
                  <p className="text-xs text-[#94A3B8] mt-1">{new Date(e.created_at).toLocaleString("en-GB")}</p>
                </div>
                <select
                  data-testid={`cp-vacancy-status-${e.id}`}
                  value={e.status}
                  onChange={(ev) => setStatus(e.id, ev.target.value)}
                  className="text-xs font-bold rounded-full px-4 py-2 border-2 outline-none cursor-pointer bg-white"
                  style={{ borderColor: STATUS_COLORS[e.status], color: STATUS_COLORS[e.status] }}
                >
                  {VACANCY_STATUSES.map((k) => <option key={k} value={k}>{STATUS_LABELS[k]}</option>)}
                </select>
              </div>
              <p className="mt-4 text-sm font-semibold text-[#0B192C]">{e.requirement}</p>
              {e.additional && <p className="mt-2 text-sm text-[#475569]">{e.additional}</p>}
              {e.calculator?.length > 0 && (
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-2 bg-[#F8FAFC] rounded-2xl p-4">
                  {e.calculator.map((c) => (
                    <div key={c.label}>
                      <p className="text-[10px] uppercase tracking-wider text-[#94A3B8]">{c.label}</p>
                      <p className="text-xs font-bold text-[#0B192C] mt-0.5">{c.value}</p>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-4">
                <label className={labelCls}>Agreed commercial terms (private — never shown publicly)</label>
                <div className="flex gap-3 items-start">
                  <textarea
                    data-testid={`cp-commercial-${e.id}`}
                    rows={2}
                    value={notesDraft[e.id] ?? e.commercial_notes ?? ""}
                    onChange={(ev) => setNotesDraft((d) => ({ ...d, [e.id]: ev.target.value }))}
                    placeholder="Agreed sourcing fee, payment terms, notes on the pharmacist's own charges..."
                    className={`${inputCls} resize-none flex-1`}
                  />
                  <button
                    data-testid={`cp-commercial-save-${e.id}`}
                    onClick={() => saveNote(e.id)}
                    disabled={savingNote === e.id}
                    className="shrink-0 inline-flex items-center gap-2 bg-[#0B192C] text-white text-xs font-bold rounded-full px-4 py-2.5 hover:bg-[#10B981] transition-colors disabled:opacity-60"
                  >
                    {savingNote === e.id ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Preferred supplier enquiries */}
      <div className="mt-10 mb-5">
        <p className="text-sm font-semibold text-[#475569]">{suppliers.length} preferred supplier enquir{suppliers.length === 1 ? "y" : "ies"}</p>
      </div>
      {suppliers.length === 0 ? (
        <div className="bg-white border border-[#E2E8F0] rounded-3xl p-14 text-center text-sm text-[#64748B]" data-testid="cp-suppliers-empty">
          No preferred supplier enquiries yet.
        </div>
      ) : (
        <div className="space-y-4">
          {suppliers.map((e) => (
            <div key={e.id} data-testid={`cp-supplier-row-${e.id}`} className="bg-white border border-[#E2E8F0] rounded-3xl p-6 sm:p-7">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="font-display font-bold text-[#0B192C]">
                    {e.organisation}
                    <span className="ml-2 text-[10px] font-extrabold uppercase tracking-wider bg-[#10B981]/15 text-[#0d9668] rounded-full px-2.5 py-1">Preferred Supplier</span>
                  </p>
                  <p className="text-sm text-[#475569] mt-1">{e.full_name} — {e.email} — {e.phone}</p>
                  <p className="text-xs text-[#94A3B8] mt-1">{new Date(e.created_at).toLocaleString("en-GB")}</p>
                </div>
                <select
                  data-testid={`cp-supplier-status-${e.id}`}
                  value={e.status}
                  onChange={(ev) => setStatus(e.id, ev.target.value)}
                  className="text-xs font-bold rounded-full px-4 py-2 border-2 outline-none cursor-pointer bg-white"
                  style={{ borderColor: STATUS_COLORS[e.status], color: STATUS_COLORS[e.status] }}
                >
                  {["new", "contacted", "proposal_sent", "contract_agreed", "unsuccessful"].map((k) => <option key={k} value={k}>{STATUS_LABELS[k]}</option>)}
                </select>
              </div>
              {e.additional && <p className="mt-3 text-sm text-[#475569]">{e.additional}</p>}
              {e.calculator?.length > 0 && (
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-2 bg-[#F8FAFC] rounded-2xl p-4">
                  {e.calculator.map((c) => (
                    <div key={c.label}>
                      <p className="text-[10px] uppercase tracking-wider text-[#94A3B8]">{c.label}</p>
                      <p className="text-xs font-bold text-[#0B192C] mt-0.5">{c.value}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ClinicTab = ({ authed }) => {
  const [enquiries, setEnquiries] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/admin/enquiries`, authed()).then((r) => setEnquiries(r.data)).catch(() => toast.error("Could not load clinic leads"));
  }, [authed]);

  useEffect(load, [load]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/enquiries/${id}`, { status }, authed());
      setEnquiries((list) => list.map((e) => (e.id === id ? { ...e, status } : e)));
      toast.success("Status updated");
    } catch {
      toast.error("Could not update status");
    }
  };

  const downloadCsv = async () => {
    try {
      const r = await axios.get(`${API}/admin/enquiries/csv?journey=ncs-clinic`, { ...authed(), responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "ncs-clinic-expressions-of-interest.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("CSV download failed");
    }
  };

  if (!enquiries) return <Loader2 size={24} className="animate-spin text-[#10B981] mx-auto mt-16" />;
  const leads = enquiries.filter((e) => e.journey === "ncs-clinic");

  return (
    <div data-testid="admin-clinic">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
        <div>
          <p className="text-sm font-semibold text-[#475569]">{leads.length} expression{leads.length === 1 ? "" : "s"} of interest</p>
          <p className="text-xs text-[#94A3B8] mt-1">
            NCS Clinic registrations, tagged by applicant type and area. Manage the lead pipeline with
            the status dropdown on each card.
          </p>
        </div>
        <button data-testid="clinic-csv-btn" onClick={downloadCsv}
          className="inline-flex items-center gap-2 text-sm font-bold bg-[#0B192C] text-white rounded-full px-5 py-2.5 hover:bg-[#10B981] transition-colors">
          <Download size={15} /> Leads CSV
        </button>
      </div>
      {leads.length === 0 ? (
        <div className="bg-white border border-[#E2E8F0] rounded-3xl p-14 text-center text-sm text-[#64748B]" data-testid="clinic-leads-empty">
          No expressions of interest yet. Registrations from /ncs-clinic will appear here.
        </div>
      ) : (
        <div className="space-y-4">
          {leads.map((e) => (
            <div key={e.id} data-testid={`clinic-lead-row-${e.id}`} className="bg-white border border-[#E2E8F0] rounded-3xl p-6 sm:p-7">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="font-display font-bold text-[#0B192C]">
                    {e.full_name} <span className="text-[#64748B] font-medium">· {e.organisation}</span>
                  </p>
                  <p className="text-sm text-[#475569] mt-1">{e.email} — {e.phone}</p>
                  <p className="text-xs text-[#94A3B8] mt-1">{new Date(e.created_at).toLocaleString("en-GB")}</p>
                </div>
                <select
                  data-testid={`clinic-lead-status-${e.id}`}
                  value={e.status}
                  onChange={(ev) => setStatus(e.id, ev.target.value)}
                  className="text-xs font-bold rounded-full px-4 py-2 border-2 outline-none cursor-pointer bg-white"
                  style={{ borderColor: STATUS_COLORS[e.status], color: STATUS_COLORS[e.status] }}
                >
                  {CLINIC_STATUSES.map((k) => <option key={k} value={k}>{STATUS_LABELS[k]}</option>)}
                </select>
              </div>
              <p className="mt-4 text-sm font-semibold text-[#0B192C]">{e.requirement}</p>
              {e.additional && <p className="mt-2 text-sm text-[#475569]">{e.additional}</p>}
              {e.calculator?.length > 0 && (
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-2 bg-[#F8FAFC] rounded-2xl p-4">
                  {e.calculator.map((c) => (
                    <div key={c.label}>
                      <p className="text-[10px] uppercase tracking-wider text-[#94A3B8]">{c.label}</p>
                      <p className="text-xs font-bold text-[#0B192C] mt-0.5">{c.value}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const CAREER_STATUSES = ["new", "reviewing", "contacted", "shortlisted", "kept_on_file", "not_proceeding"];

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const CareersTab = ({ authed }) => {
  const [entries, setEntries] = useState(null);
  const [profession, setProfession] = useState("");
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  const [privacy, setPrivacy] = useState(null);
  const [savingPrivacy, setSavingPrivacy] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (profession) params.set("profession", profession);
    if (qDebounced) params.set("q", qDebounced);
    axios.get(`${API}/admin/careers/applications?${params}`, authed())
      .then((r) => setEntries(r.data))
      .catch(() => toast.error("Could not load applications"));
  }, [authed, profession, qDebounced]);

  useEffect(load, [load]);

  useEffect(() => {
    axios.get(`${API}/admin/settings`, authed())
      .then((r) => setPrivacy({
        careers_privacy_notice: r.data.careers_privacy_notice || "",
        privacy_policy_url: r.data.privacy_policy_url || "",
      }))
      .catch(() => {});
  }, [authed]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/careers/applications/${id}`, { status }, authed());
      setEntries((list) => list.map((e) => (e.id === id ? { ...e, status } : e)));
      toast.success("Status updated");
    } catch {
      toast.error("Could not update status");
    }
  };

  const setNotes = (id, notes) =>
    setEntries((list) => list.map((e) => (e.id === id ? { ...e, internal_notes: notes } : e)));

  const saveNotes = async (id, notes) => {
    try {
      await axios.patch(`${API}/admin/careers/applications/${id}/notes`, { internal_notes: notes }, authed());
      toast.success("Notes saved");
    } catch {
      toast.error("Could not save notes");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this application and its uploaded CV? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/careers/applications/${id}`, authed());
      setEntries((list) => list.filter((e) => e.id !== id));
      toast.success("Application deleted");
    } catch {
      toast.error("Could not delete application");
    }
  };

  const downloadCv = async (app) => {
    try {
      const r = await axios.get(`${API}/admin/careers/applications/${app.id}/cv`, { ...authed(), responseType: "blob" });
      downloadBlob(r.data, app.cv_filename || "cv");
    } catch {
      toast.error("Could not download CV");
    }
  };

  const exportCsv = async () => {
    try {
      const r = await axios.get(`${API}/admin/careers/applications-export/csv`, { ...authed(), responseType: "blob" });
      downloadBlob(r.data, "ncs-careers-applications.csv");
    } catch {
      toast.error("Could not export applications");
    }
  };

  const savePrivacy = async () => {
    setSavingPrivacy(true);
    try {
      await axios.put(`${API}/admin/settings`, privacy, authed());
      toast.success("Privacy wording saved");
    } catch {
      toast.error("Could not save privacy wording");
    } finally {
      setSavingPrivacy(false);
    }
  };

  if (!entries) return <Loader2 size={24} className="animate-spin text-[#10B981] mx-auto mt-16" />;

  return (
    <div data-testid="admin-careers">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <p className="text-sm font-semibold text-[#475569]">{entries.length} application{entries.length === 1 ? "" : "s"}</p>
          <p className="text-xs text-[#94A3B8] mt-1">
            Pharmacist and pharmacy technician applications from the Join Our Pharmacy Team page, including uploaded CVs.
          </p>
        </div>
        <button onClick={exportCsv} data-testid="careers-export-csv"
          className="inline-flex items-center gap-2 text-xs font-bold text-[#0B192C] border border-[#E2E8F0] bg-white rounded-full px-4 py-2.5 hover:border-[#10B981] hover:text-[#10B981] transition-colors">
          <Download size={13} /> Export CSV
        </button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#94A3B8] pointer-events-none" />
          <input
            data-testid="careers-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or email…"
            aria-label="Search applications by name or email"
            className={`${inputCls} pl-10`}
          />
        </div>
        <select
          data-testid="careers-filter-profession"
          value={profession}
          onChange={(e) => setProfession(e.target.value)}
          aria-label="Filter by profession"
          className={`${inputCls} sm:w-56`}
        >
          <option value="">All professions</option>
          <option value="Pharmacist">Pharmacist</option>
          <option value="Pharmacy Technician">Pharmacy Technician</option>
          <option value="Other">Other</option>
        </select>
      </div>

      {entries.length === 0 ? (
        <div className="bg-white border border-[#E2E8F0] rounded-3xl p-14 text-center text-sm text-[#64748B]" data-testid="admin-careers-empty">
          No applications found. New applications from /careers will appear here.
        </div>
      ) : (
        <div className="space-y-4">
          {entries.map((e) => (
            <div key={e.id} data-testid={`careers-row-${e.id}`} className="bg-white border border-[#E2E8F0] rounded-3xl p-6 sm:p-7">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="font-display font-bold text-[#0B192C]">
                    {e.full_name} <span className="text-[#64748B] font-medium">· {e.profession}</span>
                  </p>
                  <p className="text-sm text-[#475569] mt-1">{e.email} — {e.phone}</p>
                  <p className="text-xs text-[#94A3B8] mt-1">{new Date(e.created_at).toLocaleString("en-GB")}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => downloadCv(e)} data-testid={`careers-download-cv-${e.id}`}
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-[#0B192C] border border-[#E2E8F0] bg-white rounded-full px-4 py-2 hover:border-[#06B6D4] hover:text-[#06B6D4] transition-colors">
                    <FileText size={13} /> {e.cv_filename || "Download CV"}
                  </button>
                  <select
                    data-testid={`careers-status-${e.id}`}
                    value={e.status}
                    onChange={(ev) => setStatus(e.id, ev.target.value)}
                    className="text-xs font-bold rounded-full px-4 py-2 border-2 outline-none cursor-pointer bg-white"
                    style={{ borderColor: STATUS_COLORS[e.status], color: STATUS_COLORS[e.status] }}
                  >
                    {CAREER_STATUSES.map((k) => <option key={k} value={k}>{STATUS_LABELS[k]}</option>)}
                  </select>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-2 bg-[#F8FAFC] rounded-2xl p-4">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-[#94A3B8]">Registration</p>
                  <p className="text-xs font-bold text-[#0B192C] mt-0.5">{e.registration_number || "-"}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-[#94A3B8]">Experience</p>
                  <p className="text-xs font-bold text-[#0B192C] mt-0.5">{e.experience_years || "-"}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-[#94A3B8]">Arrangement</p>
                  <p className="text-xs font-bold text-[#0B192C] mt-0.5">{e.work_arrangement || "-"}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-[#94A3B8]">Preferred location</p>
                  <p className="text-xs font-bold text-[#0B192C] mt-0.5">{e.preferred_location || "-"}</p>
                </div>
              </div>

              {e.cover_message && (
                <div className="mt-4">
                  <p className="text-[10px] uppercase tracking-wider text-[#94A3B8] mb-1">Cover message</p>
                  <p className="text-sm text-[#475569] leading-relaxed bg-[#F8FAFC] rounded-2xl p-4 whitespace-pre-wrap" data-testid={`careers-cover-${e.id}`}>
                    {e.cover_message}
                  </p>
                </div>
              )}

              <div className="mt-4 flex flex-col sm:flex-row gap-3 items-stretch sm:items-end">
                <div className="flex-1">
                  <label htmlFor={`notes-${e.id}`} className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-bold">Internal notes</label>
                  <textarea
                    id={`notes-${e.id}`}
                    data-testid={`careers-notes-${e.id}`}
                    rows={2}
                    value={e.internal_notes || ""}
                    onChange={(ev) => setNotes(e.id, ev.target.value)}
                    placeholder="Private notes — only visible to administrators"
                    className={`${inputCls} resize-none mt-1`}
                  />
                </div>
                <div className="flex gap-2">
                  <button onClick={() => saveNotes(e.id, e.internal_notes || "")} data-testid={`careers-save-notes-${e.id}`}
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-[#0B192C] border border-[#E2E8F0] bg-white rounded-full px-4 py-2.5 hover:border-[#10B981] hover:text-[#10B981] transition-colors">
                    <Save size={13} /> Save notes
                  </button>
                  <button onClick={() => remove(e.id)} data-testid={`careers-delete-${e.id}`}
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-[#EF4444] border border-[#E2E8F0] bg-white rounded-full px-4 py-2.5 hover:border-[#EF4444] transition-colors">
                    <Trash2 size={13} /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {privacy && (
        <div className="mt-8 bg-white border border-[#E2E8F0] rounded-3xl p-6 sm:p-7" data-testid="careers-privacy-settings">
          <p className="font-display text-sm font-extrabold uppercase tracking-[0.16em] text-[#06B6D4]">Recruitment privacy notice</p>
          <p className="text-xs text-[#94A3B8] mt-1">Shown on the /careers page beside the consent checkbox.</p>
          <div className="mt-4 space-y-4">
            <div>
              <label htmlFor="privacy-notice" className={labelCls}>Privacy wording</label>
              <textarea id="privacy-notice" data-testid="careers-privacy-notice-input" rows={3} maxLength={1000}
                value={privacy.careers_privacy_notice}
                onChange={(e) => setPrivacy((p) => ({ ...p, careers_privacy_notice: e.target.value }))}
                className={`${inputCls} resize-none`} />
            </div>
            <div>
              <label htmlFor="privacy-url" className={labelCls}>Privacy policy link <span className="normal-case font-medium text-[#94A3B8]">(leave blank to show a placeholder)</span></label>
              <input id="privacy-url" data-testid="careers-privacy-url-input" maxLength={300}
                value={privacy.privacy_policy_url}
                onChange={(e) => setPrivacy((p) => ({ ...p, privacy_policy_url: e.target.value }))}
                placeholder="https://nationalclinicalservices.co.uk/privacy-policy" className={inputCls} />
            </div>
            <button onClick={savePrivacy} disabled={savingPrivacy} data-testid="careers-privacy-save"
              className="inline-flex items-center gap-2 text-xs font-bold text-white bg-[#0B192C] rounded-full px-5 py-2.5 hover:bg-[#10B981] transition-colors disabled:opacity-60">
              {savingPrivacy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save privacy wording
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const SITE_IMAGE_SLOTS = [
  { key: "founder", label: "Founder portrait — Yasin Patel", desc: "Our Leadership (homepage) and founder card (About).", fallback: null, square: true },
  { key: "clinic_hero", label: "NCS Clinic — hero photograph", desc: "Hero consultation image on /ncs-clinic.", fallback: "/assets/clinic-hero.png" },
  { key: "clinic_pharmacy", label: "NCS Clinic — pharmacy consultation room", desc: "Community pharmacy section image.", fallback: "/assets/clinic-pharmacy.png" },
  { key: "clinic_prescriber", label: "NCS Clinic — prescriber consultation", desc: "Independent prescriber section image.", fallback: "/assets/clinic-prescriber.png" },
  { key: "clinic_partners", label: "NCS Clinic — partnership meeting", desc: "Founding partners section image.", fallback: "/assets/clinic-partners.png" },
  { key: "clinic_online", label: "NCS Clinic — online consultation", desc: "Digital platform section image.", fallback: "/assets/clinic-online.png" },
];

const SiteImagesTab = ({ authed }) => {
  const [content, setContent] = useState(null);
  const [busy, setBusy] = useState(null);
  const [bust, setBust] = useState(0);
  const fileRefs = useRef({});

  const load = useCallback(() => {
    axios.get(`${API}/site/content`).then((r) => setContent(r.data)).catch(() => toast.error("Could not load site imagery settings"));
  }, []);

  useEffect(load, [load]);

  const upload = async (slot, file) => {
    if (!file) return;
    setBusy(slot);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/admin/site/image/${slot}`, fd, authed());
      toast.success("Image updated — live on the site now");
      setBust((b) => b + 1);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(null);
    }
  };

  const reset = async (slot) => {
    setBusy(slot);
    try {
      await axios.delete(`${API}/admin/site/image/${slot}`, authed());
      toast.success("Reset to the default image");
      setBust((b) => b + 1);
      load();
    } catch {
      toast.error("Could not reset image");
    } finally {
      setBusy(null);
    }
  };

  if (!content) return <Loader2 size={24} className="animate-spin text-[#10B981] mx-auto mt-16" />;

  return (
    <div data-testid="admin-site-images">
      <div className="mb-5">
        <p className="text-sm font-semibold text-[#475569]">Corporate & NCS Clinic imagery</p>
        <p className="text-xs text-[#94A3B8] mt-1">
          Upload approved NCS photographs for key corporate and clinic sections. Uploads are optimised
          to WebP and go live immediately. Use Reset to return to the default image.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
        {SITE_IMAGE_SLOTS.map((s) => {
          const custom = content[`${s.key}_custom`];
          return (
            <div key={s.key} className="bg-white border border-[#E2E8F0] rounded-3xl p-6">
              {custom ? (
                <img
                  src={`${API}/site/image/${s.key}?v=${bust}`}
                  alt={s.label}
                  data-testid={`site-image-${s.key}`}
                  className={`w-full ${s.square ? "aspect-square" : "h-40"} object-cover rounded-2xl border border-[#E2E8F0]`}
                />
              ) : s.fallback ? (
                <img
                  src={s.fallback}
                  alt={s.label}
                  data-testid={`site-image-${s.key}`}
                  className={`w-full ${s.square ? "aspect-square" : "h-40"} object-cover rounded-2xl border border-[#E2E8F0]`}
                />
              ) : (
                <div data-testid={`site-image-${s.key}`}
                  className="w-full aspect-square rounded-2xl bg-[#0B192C] flex items-center justify-center border border-[#E2E8F0]">
                  <span className="font-display text-5xl font-extrabold text-[#10B981]">YP</span>
                </div>
              )}
              <div className="mt-4 flex items-center justify-between gap-3">
                <div>
                  <p className="font-display font-bold text-[#0B192C] text-sm">{s.label}</p>
                  <p className="text-xs text-[#64748B] mt-0.5">{s.desc}</p>
                  <p className="text-[10px] uppercase tracking-wider font-bold mt-1.5" style={{ color: custom ? "#10B981" : "#94A3B8" }}>
                    {custom ? "Custom image active" : "Default image active"}
                  </p>
                </div>
                <div className="shrink-0 flex flex-col items-end gap-2">
                  <button
                    data-testid={`site-upload-${s.key}`}
                    onClick={() => fileRefs.current[s.key]?.click()}
                    disabled={busy === s.key}
                    className="inline-flex items-center gap-2 bg-[#0B192C] text-white text-xs font-bold rounded-full px-4 py-2.5 hover:bg-[#10B981] transition-colors disabled:opacity-60"
                  >
                    {busy === s.key ? <Loader2 size={13} className="animate-spin" /> : <ImagePlus size={13} />}
                    {custom ? "Replace" : "Upload"}
                  </button>
                  {custom && (
                    <button data-testid={`site-reset-${s.key}`} onClick={() => reset(s.key)} disabled={busy === s.key}
                      className="text-[11px] font-bold text-[#64748B] hover:text-[#EF4444] transition-colors disabled:opacity-60">
                      Reset to default
                    </button>
                  )}
                </div>
              </div>
              <input
                ref={(el) => (fileRefs.current[s.key] = el)}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                data-testid={`site-file-${s.key}`}
                onChange={(e) => { upload(s.key, e.target.files?.[0]); e.target.value = ""; }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

const TransportTab = ({ authed }) => {
  const [photos, setPhotos] = useState(null);
  const [content, setContent] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [savingContent, setSavingContent] = useState(false);
  const uploadRef = useRef(null);
  const replaceRef = useRef(null);
  const [replaceTarget, setReplaceTarget] = useState(null);

  const loadPhotos = useCallback(() => {
    axios.get(`${API}/admin/fleet`, authed()).then((r) => setPhotos(r.data)).catch(() => toast.error("Could not load fleet photos"));
  }, [authed]);
  const loadContent = useCallback(() => {
    axios.get(`${API}/transport/content`).then((r) => setContent({ ...r.data, areas_text: (r.data.areas || []).join(", ") })).catch(() => toast.error("Could not load transport content"));
  }, []);

  useEffect(() => {
    loadPhotos();
    loadContent();
  }, [loadPhotos, loadContent]);

  const uploadFiles = async (fileList) => {
    if (!fileList?.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      [...fileList].forEach((f) => fd.append("files", f));
      await axios.post(`${API}/admin/fleet/upload`, fd, authed());
      toast.success(`${fileList.length} photo${fileList.length > 1 ? "s" : ""} uploaded`);
      loadPhotos();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Upload failed");
    } finally {
      setUploading(false);
      if (uploadRef.current) uploadRef.current.value = "";
    }
  };

  const replaceFile = async (fileList) => {
    if (!fileList?.length || !replaceTarget) return;
    try {
      const fd = new FormData();
      fd.append("file", fileList[0]);
      await axios.post(`${API}/admin/fleet/${replaceTarget}/replace`, fd, authed());
      toast.success("Photograph replaced");
      loadPhotos();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Replace failed");
    } finally {
      setReplaceTarget(null);
      if (replaceRef.current) replaceRef.current.value = "";
    }
  };

  const move = async (index, dir) => {
    const next = [...photos];
    const j = index + dir;
    if (j < 0 || j >= next.length) return;
    [next[index], next[j]] = [next[j], next[index]];
    setPhotos(next);
    try {
      await axios.post(`${API}/admin/fleet/reorder`, { ids: next.map((p) => p.id) }, authed());
    } catch {
      toast.error("Could not save order");
      loadPhotos();
    }
  };

  const update = async (p, patch, msg) => {
    try {
      await axios.put(`${API}/admin/fleet/${p.id}`, patch, authed());
      if (msg) toast.success(msg);
      loadPhotos();
    } catch {
      toast.error("Could not save");
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/fleet/${p.id}`, authed());
      toast.success("Photograph deleted");
      loadPhotos();
    } catch {
      toast.error("Could not delete");
    }
  };

  const saveEdit = async () => {
    try {
      await axios.put(`${API}/admin/fleet/${editing.id}`, {
        title: editing.title,
        description: editing.description,
        alt: editing.alt,
      }, authed());
      toast.success("Photograph details saved");
      setEditing(null);
      loadPhotos();
    } catch {
      toast.error("Could not save details");
    }
  };

  const saveContent = async () => {
    setSavingContent(true);
    try {
      await axios.put(`${API}/admin/transport/content`, {
        contact_email: content.contact_email,
        areas: content.areas_text.split(",").map((a) => a.trim()).filter(Boolean),
        desc_home_school: content.desc_home_school,
        desc_send: content.desc_send,
        desc_nept: content.desc_nept,
        desc_contracted: content.desc_contracted,
      }, authed());
      toast.success("Transport page content saved — live on the website");
    } catch {
      toast.error("Could not save content");
    } finally {
      setSavingContent(false);
    }
  };

  if (!photos || !content) return <Loader2 size={24} className="animate-spin text-[#10B981] mx-auto mt-16" />;

  const ctrlBtn = "w-9 h-9 rounded-full border border-[#E2E8F0] bg-white flex items-center justify-center text-[#475569] hover:border-[#0B192C] hover:text-[#0B192C] transition-colors";

  return (
    <div className="space-y-8" data-testid="admin-transport">
      {/* GALLERY */}
      <div className="bg-white border border-[#E2E8F0] rounded-3xl p-7">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h3 className="font-display text-lg font-bold text-[#0B192C]">Fleet gallery</h3>
            <p className="text-xs text-[#64748B] mt-1">
              Upload minibus photographs from your phone or computer — JPG, PNG or WebP, several at
              once. Images are automatically optimised for the website.
            </p>
          </div>
          <button
            data-testid="fleet-upload-btn"
            onClick={() => uploadRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-2 text-sm font-bold bg-[#0B192C] text-white rounded-full px-5 py-2.5 hover:bg-[#10B981] transition-colors disabled:opacity-60"
          >
            {uploading ? <Loader2 size={15} className="animate-spin" /> : <ImagePlus size={15} />}
            {uploading ? "Uploading..." : "Upload photographs"}
          </button>
          <input
            ref={uploadRef}
            data-testid="fleet-upload-input"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            className="hidden"
            onChange={(e) => uploadFiles(e.target.files)}
          />
          <input
            ref={replaceRef}
            data-testid="fleet-replace-input"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => replaceFile(e.target.files)}
          />
        </div>

        <div className="mt-4 flex items-start gap-2.5 bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-2xl px-4 py-3">
          <AlertTriangle size={15} className="text-[#B45309] shrink-0 mt-0.5" />
          <p className="text-xs text-[#475569] leading-relaxed">
            Do not publish vehicle photographs containing identifiable children or passengers
            without appropriate permission.
          </p>
        </div>

        {photos.length === 0 ? (
          <div className="mt-6 border-2 border-dashed border-[#E2E8F0] rounded-2xl p-12 text-center text-sm text-[#64748B]" data-testid="fleet-admin-empty">
            No photographs yet — upload your first minibus photos to fill the gallery.
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {photos.map((p, i) => (
              <div key={p.id} data-testid={`fleet-admin-card-${p.id}`} className="border border-[#E2E8F0] rounded-2xl overflow-hidden bg-[#FAFAFC]">
                <div className="relative">
                  <img src={`${API}/fleet/image/${p.id}`} alt={p.alt || p.title} className="w-full h-44 object-cover" />
                  <div className="absolute top-3 left-3 flex gap-2">
                    {p.featured && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-wider bg-[#F59E0B] text-[#0B192C] rounded-full px-2.5 py-1">
                        <Star size={10} className="fill-[#0B192C]" /> Featured
                      </span>
                    )}
                    {!p.published && (
                      <span className="text-[10px] font-extrabold uppercase tracking-wider bg-[#64748B] text-white rounded-full px-2.5 py-1">Hidden</span>
                    )}
                  </div>
                </div>
                <div className="p-4">
                  <p className="font-display font-bold text-sm text-[#0B192C] truncate">{p.title}</p>
                  <div className="mt-3 flex items-center justify-between">
                    <div className="flex gap-1.5">
                      <button aria-label="Move up" data-testid={`fleet-up-${p.id}`} onClick={() => move(i, -1)} disabled={i === 0} className={`${ctrlBtn} disabled:opacity-30`}>
                        <ArrowUp size={14} />
                      </button>
                      <button aria-label="Move down" data-testid={`fleet-down-${p.id}`} onClick={() => move(i, 1)} disabled={i === photos.length - 1} className={`${ctrlBtn} disabled:opacity-30`}>
                        <ArrowDown size={14} />
                      </button>
                    </div>
                    <div className="flex gap-1.5">
                      <button aria-label="Set featured" title="Set as featured" data-testid={`fleet-feature-${p.id}`} onClick={() => update(p, { featured: true }, "Featured vehicle updated")} className={`${ctrlBtn} ${p.featured ? "text-[#F59E0B] border-[#F59E0B]" : ""}`}>
                        <Star size={14} />
                      </button>
                      <button aria-label={p.published ? "Hide" : "Publish"} title={p.published ? "Hide from website" : "Publish to website"} data-testid={`fleet-publish-${p.id}`} onClick={() => update(p, { published: !p.published })} className={ctrlBtn}>
                        {p.published ? <Eye size={14} /> : <EyeOff size={14} />}
                      </button>
                      <button aria-label="Edit details" title="Edit details" data-testid={`fleet-edit-${p.id}`} onClick={() => setEditing({ ...p })} className={ctrlBtn}>
                        <Pencil size={14} />
                      </button>
                      <button aria-label="Replace photo" title="Replace photo" data-testid={`fleet-replace-${p.id}`} onClick={() => { setReplaceTarget(p.id); replaceRef.current?.click(); }} className={ctrlBtn}>
                        <RefreshCw size={14} />
                      </button>
                      <button aria-label="Delete photo" title="Delete" data-testid={`fleet-delete-${p.id}`} onClick={() => remove(p)} className={`${ctrlBtn} hover:!border-[#EF4444] hover:!text-[#EF4444]`}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* EDIT MODAL */}
      {editing && (
        <div className="fixed inset-0 z-[200] bg-[#0B192C]/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-3xl p-7 w-full max-w-lg" data-testid="fleet-edit-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-lg font-bold text-[#0B192C]">Photograph details</h3>
            <div className="mt-5 space-y-4">
              <div>
                <label className={labelCls} htmlFor="fleet-edit-title">Vehicle title / registration</label>
                <input id="fleet-edit-title" data-testid="fleet-edit-title" value={editing.title}
                  onChange={(e) => setEditing((p) => ({ ...p, title: e.target.value }))} className={inputCls} />
              </div>
              <div>
                <label className={labelCls} htmlFor="fleet-edit-desc">Description (optional)</label>
                <textarea id="fleet-edit-desc" data-testid="fleet-edit-desc" rows={3} value={editing.description}
                  onChange={(e) => setEditing((p) => ({ ...p, description: e.target.value }))} className={`${inputCls} resize-none`} />
              </div>
              <div>
                <label className={labelCls} htmlFor="fleet-edit-alt">Alt text (accessibility)</label>
                <input id="fleet-edit-alt" data-testid="fleet-edit-alt" value={editing.alt}
                  onChange={(e) => setEditing((p) => ({ ...p, alt: e.target.value }))}
                  placeholder="e.g. White NCS minibus parked outside a school" className={inputCls} />
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              <button data-testid="fleet-edit-save" onClick={saveEdit}
                className="inline-flex items-center gap-2 bg-[#0B192C] text-white font-bold px-6 py-3 rounded-full text-sm hover:bg-[#10B981] transition-colors">
                <Save size={14} /> Save details
              </button>
              <button data-testid="fleet-edit-cancel" onClick={() => setEditing(null)}
                className="text-sm font-bold text-[#475569] px-5 py-3 rounded-full border border-[#E2E8F0] hover:border-[#0B192C] transition-colors">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CONTENT */}
      <div className="bg-white border border-[#E2E8F0] rounded-3xl p-7" data-testid="transport-content-editor">
        <h3 className="font-display text-lg font-bold text-[#0B192C]">Transport page content</h3>
        <p className="text-xs text-[#64748B] mt-1">Changes go live on the Transport Services page as soon as you save.</p>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls} htmlFor="tc-email">Transport contact email</label>
            <input id="tc-email" data-testid="tc-input-email" type="email" value={content.contact_email}
              onChange={(e) => setContent((c) => ({ ...c, contact_email: e.target.value }))} className={inputCls} />
          </div>
          <div>
            <label className={labelCls} htmlFor="tc-areas">Operating areas (comma separated)</label>
            <input id="tc-areas" data-testid="tc-input-areas" value={content.areas_text}
              onChange={(e) => setContent((c) => ({ ...c, areas_text: e.target.value }))}
              placeholder="Wigan, Bolton, Bury..." className={inputCls} />
          </div>
          {[
            ["desc_home_school", "Home-to-school transport description"],
            ["desc_send", "SEND & specialist transport description"],
            ["desc_nept", "Non-emergency patient transport description"],
            ["desc_contracted", "Contracted transport description"],
          ].map(([key, label]) => (
            <div key={key} className="sm:col-span-1">
              <label className={labelCls} htmlFor={`tc-${key}`}>{label}</label>
              <textarea id={`tc-${key}`} data-testid={`tc-${key}`} rows={4} value={content[key]}
                onChange={(e) => setContent((c) => ({ ...c, [key]: e.target.value }))}
                className={`${inputCls} resize-none`} />
            </div>
          ))}
        </div>
        <button
          data-testid="tc-save-btn"
          onClick={saveContent}
          disabled={savingContent}
          className="mt-6 inline-flex items-center gap-2 bg-[#0B192C] text-white font-bold px-7 py-3.5 rounded-full text-sm hover:bg-[#10B981] transition-colors disabled:opacity-60"
        >
          {savingContent ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save transport content
        </button>
      </div>
    </div>
  );
};

const ARTICLE_CATEGORIES = ["Medicines Optimisation", "ARRS & Workforce", "Prescribing Safety", "Primary Care"];

const blankArticle = () => ({
  title: "",
  category: "Primary Care",
  excerpt: "",
  read_time: 5,
  published_at: new Date().toISOString().slice(0, 10),
  content_text: "",
});

const ArticlesTab = ({ authed }) => {
  const [articles, setArticles] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/admin/articles`, authed()).then((r) => setArticles(r.data)).catch(() => toast.error("Could not load articles"));
  }, [authed]);

  useEffect(load, [load]);

  const save = async () => {
    setSaving(true);
    try {
      if (editing.id) {
        await axios.put(`${API}/admin/articles/${editing.id}`, editing, authed());
      } else {
        await axios.post(`${API}/admin/articles`, editing, authed());
      }
      toast.success(editing.id ? "Article updated" : "Article published");
      setEditing(null);
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not save article — check all fields are filled");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (article) => {
    if (!window.confirm(`Delete "${article.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/articles/${article.id}`, authed());
      toast.success("Article deleted");
      load();
    } catch {
      toast.error("Could not delete article");
    }
  };

  if (!articles) return <Loader2 size={24} className="animate-spin text-[#10B981] mx-auto mt-16" />;

  if (editing) {
    const set = (k) => (e) => setEditing((a) => ({ ...a, [k]: e.target.value }));
    return (
      <div className="bg-white border border-[#E2E8F0] rounded-3xl p-7" data-testid="article-editor">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-[#0B192C]">
            {editing.id ? "Edit article" : "New article"}
          </h3>
          <button
            data-testid="article-back-btn"
            onClick={() => setEditing(null)}
            className="inline-flex items-center gap-2 text-sm font-bold text-[#475569] hover:text-[#0B192C] transition-colors"
          >
            <ArrowLeft size={15} /> Back to list
          </button>
        </div>
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className={labelCls} htmlFor="art-title">Title *</label>
            <input id="art-title" data-testid="article-input-title" value={editing.title} onChange={set("title")}
              placeholder="e.g. Making the Most of Care Home Review Cycles" className={inputCls} />
          </div>
          <div>
            <label className={labelCls} htmlFor="art-category">Category</label>
            <select id="art-category" data-testid="article-select-category" value={editing.category} onChange={set("category")} className={inputCls}>
              {ARTICLE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls} htmlFor="art-readtime">Read time (min)</label>
              <input id="art-readtime" data-testid="article-input-readtime" type="number" min="1" max="60"
                value={editing.read_time} onChange={set("read_time")} className={inputCls} />
            </div>
            <div>
              <label className={labelCls} htmlFor="art-date">Publish date</label>
              <input id="art-date" data-testid="article-input-date" type="date"
                value={editing.published_at} onChange={set("published_at")} className={inputCls} />
            </div>
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls} htmlFor="art-excerpt">Excerpt (shown on the insights list) *</label>
            <textarea id="art-excerpt" data-testid="article-input-excerpt" rows={2}
              value={editing.excerpt} onChange={set("excerpt")}
              placeholder="One or two sentences summarising the article..." className={`${inputCls} resize-none`} />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls} htmlFor="art-content">Article body *</label>
            <textarea id="art-content" data-testid="article-input-content" rows={14}
              value={editing.content_text} onChange={set("content_text")}
              placeholder={"Write the article here.\n\nLeave a blank line between paragraphs.\n\n## Start a line with two hashes for a section heading."}
              className={`${inputCls} resize-y font-mono text-[13px] leading-relaxed`} />
            <p className="mt-2 text-xs text-[#94A3B8]">
              Formatting: blank line = new paragraph · start a line with "## " for a section heading
            </p>
          </div>
        </div>
        <button
          data-testid="article-save-btn"
          onClick={save}
          disabled={saving}
          className="mt-6 inline-flex items-center gap-2 bg-[#0B192C] text-white font-bold px-7 py-3.5 rounded-full text-sm hover:bg-[#10B981] transition-colors disabled:opacity-60"
        >
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
          {editing.id ? "Save changes" : "Publish article"}
        </button>
      </div>
    );
  }

  return (
    <div data-testid="admin-articles">
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm font-semibold text-[#475569]">{articles.length} article{articles.length === 1 ? "" : "s"}</p>
        <button
          data-testid="article-new-btn"
          onClick={() => setEditing(blankArticle())}
          className="inline-flex items-center gap-2 text-sm font-bold bg-[#0B192C] text-white rounded-full px-5 py-2.5 hover:bg-[#10B981] transition-colors"
        >
          <Plus size={15} /> New article
        </button>
      </div>
      <div className="space-y-4">
        {articles.map((a) => (
          <div key={a.id} data-testid={`article-row-${a.slug}`} className="bg-white border border-[#E2E8F0] rounded-3xl p-6 flex flex-wrap items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="font-display font-bold text-[#0B192C]">{a.title}</p>
              <p className="text-xs text-[#94A3B8] mt-1">
                {a.category} · {a.read_time} min read · {new Date(a.published_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })} · /insights/{a.slug}
              </p>
            </div>
            <div className="flex items-center gap-2.5 shrink-0">
              <a
                href={`/insights/${a.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`article-view-${a.slug}`}
                className="text-xs font-bold text-[#06B6D4] border-2 border-[#06B6D4] rounded-full px-4 py-2 hover:bg-[#06B6D4] hover:text-white transition-colors"
              >
                View
              </a>
              <button
                data-testid={`article-edit-${a.slug}`}
                onClick={() => setEditing(a)}
                className="inline-flex items-center gap-1.5 text-xs font-bold text-[#0B192C] border-2 border-[#E2E8F0] rounded-full px-4 py-2 hover:border-[#0B192C] transition-colors"
              >
                <Pencil size={12} /> Edit
              </button>
              <button
                data-testid={`article-delete-${a.slug}`}
                onClick={() => remove(a)}
                className="inline-flex items-center gap-1.5 text-xs font-bold text-[#EF4444] border-2 border-[#EF4444]/30 rounded-full px-4 py-2 hover:bg-[#EF4444] hover:text-white hover:border-[#EF4444] transition-colors"
              >
                <Trash2 size={12} /> Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const PricingTab = ({ authed }) => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/settings`, authed()).then((r) => setSettings(r.data)).catch(() => toast.error("Could not load settings"));
  }, [authed]);

  if (!settings) return <Loader2 size={24} className="animate-spin text-[#10B981] mx-auto mt-16" />;

  const setRate = (k, v) => setSettings((s) => ({ ...s, rates: { ...s.rates, [k]: Number(v) } }));
  const setCost = (k, v) => setSettings((s) => ({ ...s, internal_costs: { ...s.internal_costs, [k]: Number(v) } }));
  const setIntlRate = (k, v) => setSettings((s) => ({ ...s, international_rates: { ...(s.international_rates || {}), [k]: Number(v) } }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/settings`, {
        rates: settings.rates,
        internal_costs: settings.internal_costs,
        project_fee: Number(settings.project_fee),
        uncontactable_pct: Number(settings.uncontactable_pct),
        complex_case_pct: Number(settings.complex_case_pct),
        international_rates: settings.international_rates,
      }, authed());
      setSettings(data);
      toast.success("Pricing saved");
    } catch {
      toast.error("Could not save pricing");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="admin-pricing">
      <div className="bg-white border border-[#E2E8F0] rounded-3xl p-7">
        <h3 className="font-display text-lg font-bold text-[#0B192C]">Customer charge-out rates (£/hour)</h3>
        <p className="text-xs text-[#64748B] mt-1">Your reference rates for preparing proposals and quotations.</p>
        <div className="mt-5 grid grid-cols-2 gap-4">
          {ROLES.map((r) => (
            <div key={r.key}>
              <label className={labelCls}>{r.label}</label>
              <input data-testid={`rate-input-${r.key}`} type="number" min="0" step="0.5"
                value={settings.rates[r.key]} onChange={(e) => setRate(r.key, e.target.value)} className={inputCls} />
            </div>
          ))}
        </div>
        <div className="mt-5 grid grid-cols-3 gap-4">
          <div>
            <label className={labelCls}>Project fee (£)</label>
            <input data-testid="rate-input-project-fee" type="number" min="0"
              value={settings.project_fee} onChange={(e) => setSettings((s) => ({ ...s, project_fee: e.target.value }))} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Uncontactable %</label>
            <input data-testid="rate-input-uncontactable" type="number" min="0" max="100"
              value={settings.uncontactable_pct} onChange={(e) => setSettings((s) => ({ ...s, uncontactable_pct: e.target.value }))} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Complex case %</label>
            <input data-testid="rate-input-complex" type="number" min="0" max="100"
              value={settings.complex_case_pct} onChange={(e) => setSettings((s) => ({ ...s, complex_case_pct: e.target.value }))} className={inputCls} />
          </div>
        </div>
      </div>
      <div className="bg-white border border-[#E2E8F0] rounded-3xl p-7">
        <h3 className="font-display text-lg font-bold text-[#0B192C]">Internal contractor costs (£/hour)</h3>
        <p className="text-xs text-[#64748B] mt-1">Never shown to website visitors — your private reference figures.</p>
        <div className="mt-5 grid grid-cols-2 gap-4">
          {ROLES.map((r) => (
            <div key={r.key}>
              <label className={labelCls}>{r.label} cost</label>
              <input data-testid={`cost-input-${r.key}`} type="number" min="0" step="0.5"
                value={settings.internal_costs[r.key]} onChange={(e) => setCost(r.key, e.target.value)} className={inputCls} />
            </div>
          ))}
        </div>
        <button
          data-testid="pricing-save-btn"
          onClick={save}
          disabled={saving}
          className="mt-7 inline-flex items-center gap-2 bg-[#0B192C] text-white font-bold px-7 py-3.5 rounded-full text-sm hover:bg-[#10B981] transition-colors disabled:opacity-60"
        >
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save pricing
        </button>
      </div>
      <div className="bg-white border border-[#E2E8F0] rounded-3xl p-7 lg:col-span-2">
        <h3 className="font-display text-lg font-bold text-[#0B192C]">International charge-out rates (per pharmacist hour)</h3>
        <p className="text-xs text-[#64748B] mt-1">
          Approved commercial rates for NCS International markets, in local currency. Leave at 0 for any
          market without an approved rate — the public calculator will show "provided on enquiry" instead
          of a price. Do not enter converted or estimated figures.
        </p>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { key: "AED", label: "UAE — Dubai (AED/hour)" },
            { key: "QAR", label: "Qatar — Doha (QAR/hour)" },
            { key: "OMR", label: "Oman — Muscat (OMR/hour)" },
          ].map((c) => (
            <div key={c.key}>
              <label className={labelCls}>{c.label}</label>
              <input data-testid={`intl-rate-input-${c.key}`} type="number" min="0" step="0.5"
                value={settings.international_rates?.[c.key] ?? 0}
                onChange={(e) => setIntlRate(c.key, e.target.value)} className={inputCls} />
            </div>
          ))}
        </div>
        <p className="mt-4 text-xs text-[#94A3B8]">Saved together with UK pricing via the Save pricing button above.</p>
      </div>
    </div>
  );
};

const ProjectSettingsTab = ({ authed }) => {
  const [times, setTimes] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/settings`, authed()).then((r) => setTimes(r.data.service_times)).catch(() => toast.error("Could not load settings"));
  }, [authed]);

  if (!times) return <Loader2 size={24} className="animate-spin text-[#10B981] mx-auto mt-16" />;

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, { service_times: times }, authed());
      toast.success("Project settings saved");
    } catch {
      toast.error("Could not save settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-3xl p-7" data-testid="admin-project-settings">
      <h3 className="font-display text-lg font-bold text-[#0B192C]">Estimated time per patient / review</h3>
      <p className="text-xs text-[#64748B] mt-1">
        Your reference figures for scoping projects — minutes per patient including preparation,
        consultation, documentation, administration and follow-up.
      </p>
      <div className="mt-6 space-y-3">
        {SERVICE_LIST.map((s) => (
          <div key={s.key} className="flex flex-wrap items-center justify-between gap-4 bg-[#F8FAFC] rounded-2xl px-5 py-4" data-testid={`time-row-${s.key}`}>
            <p className="text-sm font-semibold text-[#0B192C] min-w-[240px] flex-1">{s.label}</p>
            <div className="flex items-center gap-4">
              <div className="w-32">
                <label className="block text-[10px] font-bold uppercase tracking-wider text-[#64748B] mb-1">Pharmacist min</label>
                <input data-testid={`time-ph-${s.key}`} type="number" min="0"
                  value={times[s.key]?.pharmacist_min ?? 40}
                  onChange={(e) => setTimes((t) => ({ ...t, [s.key]: { ...t[s.key], pharmacist_min: Number(e.target.value) } }))}
                  className={`${inputCls} py-2`} />
              </div>
              <div className="w-32">
                <label className="block text-[10px] font-bold uppercase tracking-wider text-[#64748B] mb-1">Technician min</label>
                <input data-testid={`time-tech-${s.key}`} type="number" min="0"
                  value={times[s.key]?.technician_min ?? 15}
                  onChange={(e) => setTimes((t) => ({ ...t, [s.key]: { ...t[s.key], technician_min: Number(e.target.value) } }))}
                  className={`${inputCls} py-2`} />
              </div>
            </div>
          </div>
        ))}
      </div>
      <button
        data-testid="project-settings-save-btn"
        onClick={save}
        disabled={saving}
        className="mt-6 inline-flex items-center gap-2 bg-[#0B192C] text-white font-bold px-7 py-3.5 rounded-full text-sm hover:bg-[#10B981] transition-colors disabled:opacity-60"
      >
        {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save project settings
      </button>
    </div>
  );
};

export default Admin;
